import { useEffect, useState } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, Link, useNavigate, useLocation } from "react-router-dom";
import axios from "axios";
import { Toaster } from "@/components/ui/sonner";
import { toast } from "sonner";

// Import pages
import HomePage from "@/pages/HomePage";
import TrainingPage from "@/pages/TrainingPage";
import MassagePage from "@/pages/MassagePage";
import MediaPage from "@/pages/MediaPage";
import MerchPage from "@/pages/MerchPage";
import BookingPage from "@/pages/BookingPage";
import ContactPage from "@/pages/ContactPage";
import AdminLoginPage from "@/pages/AdminLoginPage";
import AdminDashboard from "@/pages/AdminDashboard";

// Logos
// Logo URLs
const LOGO_TN = "https://customer-assets.emergentagent.com/job_6b5e3bef-198e-4658-8305-ef4fd544fb4d/artifacts/prm3jnbc_IMG_1264.png";
const LOGO_SPORTS = "https://customer-assets.emergentagent.com/job_6b5e3bef-198e-4658-8305-ef4fd544fb4d/artifacts/xqwv9ghf_IMG_7268.png";
const LOGO_MASSAGE = "https://customer-assets.emergentagent.com/job_6b5e3bef-198e-4658-8305-ef4fd544fb4d/artifacts/bkt4vijm_IMG_5566.png";
const LOGO_FLIX = "https://customer-assets.emergentagent.com/job_6b5e3bef-198e-4658-8305-ef4fd544fb4d/artifacts/rjk9hmx3_IMG_6380.png";
const LOGO_HF = "https://customer-assets.emergentagent.com/job_6b5e3bef-198e-4658-8305-ef4fd544fb4d/artifacts/o4ok88eo_IMG_1930.png";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
export const API = `${BACKEND_URL}/api`;

// Navbar Component
const Navbar = () => {
  const [isOpen, setIsOpen] = useState(false);
  const location = useLocation();

  const navLinks = [
    { path: "/", label: "Home" },
    { path: "/training", label: "Training" },
    { path: "/massage", label: "Massage" },
    { path: "/media", label: "Media" },
    { path: "/merch", label: "Merch" },
    { path: "/booking", label: "Book Now" },
    { path: "/contact", label: "Contact" },
  ];

  const isActive = (path) => location.pathname === path;

  return (
    <nav className="fixed top-0 left-0 right-0 z-50 bg-[#0A0A0A]/95 backdrop-blur-sm border-b border-[#2A2A2A]">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-16 md:h-20">
          {/* Logo */}
          <Link to="/" className="flex items-center gap-2" data-testid="logo-link">
            <img 
              src="https://customer-assets.emergentagent.com/job_6b5e3bef-198e-4658-8305-ef4fd544fb4d/artifacts/prm3jnbc_IMG_1264.png" 
              alt="Top Notch" 
              className="h-10 md:h-14 w-auto"
            />
            <span className="font-display text-xl md:text-2xl text-white hidden sm:inline">TOP NOTCH</span>
          </Link>

          {/* Desktop Nav */}
          <div className="hidden md:flex items-center gap-1">
            {navLinks.map((link) => (
              <Link
                key={link.path}
                to={link.path}
                className={`nav-link text-sm ${isActive(link.path) ? "active text-[#E10600]" : "text-white"}`}
                data-testid={`nav-${link.label.toLowerCase().replace(" ", "-")}`}
              >
                {link.label}
              </Link>
            ))}
          </div>

          {/* Mobile Menu Button */}
          <button
            onClick={() => setIsOpen(!isOpen)}
            className="md:hidden p-2 bg-[#E10600] rounded-lg"
            data-testid="mobile-menu-btn"
          >
            <div className="w-6 h-5 flex flex-col justify-between">
              <span className="block w-full h-0.5 bg-white rounded"></span>
              <span className="block w-full h-0.5 bg-white rounded"></span>
              <span className="block w-full h-0.5 bg-white rounded"></span>
            </div>
          </button>
        </div>
      </div>

      {/* Mobile Menu */}
      {isOpen && (
        <div className="mobile-menu md:hidden">
          <button
            onClick={() => setIsOpen(false)}
            className="absolute top-4 right-4 p-2 text-white"
          >
            <svg className="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
          {navLinks.map((link) => (
            <Link
              key={link.path}
              to={link.path}
              onClick={() => setIsOpen(false)}
              className={`mobile-menu-link ${isActive(link.path) ? "text-[#E10600]" : ""}`}
              data-testid={`mobile-nav-${link.label.toLowerCase().replace(" ", "-")}`}
            >
              {link.label}
            </Link>
          ))}
        </div>
      )}
    </nav>
  );
};

// Footer Component
const Footer = () => {
  return (
    <footer className="bg-[#0A0A0A] border-t border-[#2A2A2A] py-12">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Brand Logos Row */}
        <div className="flex flex-wrap justify-center gap-6 md:gap-10 mb-10 pb-8 border-b border-[#2A2A2A]">
          <img src={LOGO_SPORTS} alt="Top Notch Sports" className="h-16 md:h-20 w-auto" />
          <img src={LOGO_MASSAGE} alt="Top Notch Massage" className="h-16 md:h-20 w-auto" />
          <img src={LOGO_FLIX} alt="Top Notch Flix" className="h-16 md:h-20 w-auto" />
          <img src={LOGO_HF} alt="Top Notch Health & Fitness" className="h-16 md:h-20 w-auto" />
        </div>

        <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
          {/* Services */}
          <div>
            <h4 className="font-display text-lg text-white mb-4">TRAINING</h4>
            <ul className="space-y-2 text-sm">
              <li><Link to="/training" className="text-gray-400 hover:text-[#E10600]">Youth Training</Link></li>
              <li><Link to="/training" className="text-gray-400 hover:text-[#E10600]">Team Sessions</Link></li>
              <li><Link to="/booking?category=training" className="text-gray-400 hover:text-[#E10600]">Book Training</Link></li>
            </ul>
          </div>

          {/* Massage */}
          <div>
            <h4 className="font-display text-lg text-white mb-4">MASSAGE</h4>
            <ul className="space-y-2 text-sm">
              <li><Link to="/massage" className="text-gray-400 hover:text-[#E10600]">Sports Massage</Link></li>
              <li><Link to="/massage" className="text-gray-400 hover:text-[#E10600]">Deep Tissue</Link></li>
              <li><Link to="/booking?category=massage" className="text-gray-400 hover:text-[#E10600]">Book Massage</Link></li>
            </ul>
          </div>

          {/* Media */}
          <div>
            <h4 className="font-display text-lg text-white mb-4">MEDIA</h4>
            <ul className="space-y-2 text-sm">
              <li><Link to="/media" className="text-gray-400 hover:text-[#E10600]">Photography</Link></li>
              <li><Link to="/media" className="text-gray-400 hover:text-[#E10600]">Videography</Link></li>
              <li><Link to="/booking?category=media" className="text-gray-400 hover:text-[#E10600]">Book Media</Link></li>
            </ul>
          </div>

          {/* Contact */}
          <div>
            <h4 className="font-display text-lg text-white mb-4">CONTACT</h4>
            <ul className="space-y-2 text-sm text-gray-400">
              <li><a href="mailto:topnotchhf@gmail.com" className="hover:text-[#E10600]">topnotchhf@gmail.com</a></li>
              <li><a href="tel:856-993-4916" className="hover:text-[#E10600]">(856) 993-4916</a></li>
              <li className="pt-1 text-[#E10600]">topnotchhf.com</li>
              <li className="flex gap-4 pt-3">
                <a href="https://instagram.com/topnotch_sports_" target="_blank" rel="noopener noreferrer" className="hover:text-[#E10600]" title="Sports">
                  <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24"><path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zm0-2.163c-3.259 0-3.667.014-4.947.072-4.358.2-6.78 2.618-6.98 6.98-.059 1.281-.073 1.689-.073 4.948 0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98 1.281.058 1.689.072 4.948.072 3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98-1.281-.059-1.69-.073-4.949-.073zm0 5.838c-3.403 0-6.162 2.759-6.162 6.162s2.759 6.163 6.162 6.163 6.162-2.759 6.162-6.163c0-3.403-2.759-6.162-6.162-6.162zm0 10.162c-2.209 0-4-1.79-4-4 0-2.209 1.791-4 4-4s4 1.791 4 4c0 2.21-1.791 4-4 4zm6.406-11.845c-.796 0-1.441.645-1.441 1.44s.645 1.44 1.441 1.44c.795 0 1.439-.645 1.439-1.44s-.644-1.44-1.439-1.44z"/></svg>
                </a>
                <a href="https://instagram.com/topnotch_flix" target="_blank" rel="noopener noreferrer" className="hover:text-[#E10600]" title="Flix">
                  <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24"><path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zm0-2.163c-3.259 0-3.667.014-4.947.072-4.358.2-6.78 2.618-6.98 6.98-.059 1.281-.073 1.689-.073 4.948 0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98 1.281.058 1.689.072 4.948.072 3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98-1.281-.059-1.69-.073-4.949-.073zm0 5.838c-3.403 0-6.162 2.759-6.162 6.162s2.759 6.163 6.162 6.163 6.162-2.759 6.162-6.163c0-3.403-2.759-6.162-6.162-6.162zm0 10.162c-2.209 0-4-1.79-4-4 0-2.209 1.791-4 4-4s4 1.791 4 4c0 2.21-1.791 4-4 4zm6.406-11.845c-.796 0-1.441.645-1.441 1.44s.645 1.44 1.441 1.44c.795 0 1.439-.645 1.439-1.44s-.644-1.44-1.439-1.44z"/></svg>
                </a>
                <a href="https://instagram.com/topnotch_handf" target="_blank" rel="noopener noreferrer" className="hover:text-[#E10600]" title="H&F">
                  <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24"><path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zm0-2.163c-3.259 0-3.667.014-4.947.072-4.358.2-6.78 2.618-6.98 6.98-.059 1.281-.073 1.689-.073 4.948 0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98 1.281.058 1.689.072 4.948.072 3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98-1.281-.059-1.69-.073-4.949-.073zm0 5.838c-3.403 0-6.162 2.759-6.162 6.162s2.759 6.163 6.162 6.163 6.162-2.759 6.162-6.163c0-3.403-2.759-6.162-6.162-6.162zm0 10.162c-2.209 0-4-1.79-4-4 0-2.209 1.791-4 4-4s4 1.791 4 4c0 2.21-1.791 4-4 4zm6.406-11.845c-.796 0-1.441.645-1.441 1.44s.645 1.44 1.441 1.44c.795 0 1.439-.645 1.439-1.44s-.644-1.44-1.439-1.44z"/></svg>
                </a>
              </li>
            </ul>
          </div>
        </div>

        <div className="border-t border-[#2A2A2A] mt-8 pt-8 text-center text-sm text-gray-500">
          <p>&copy; {new Date().getFullYear()} Top Notch Athletics | topnotchhf.com</p>
        </div>
      </div>
    </footer>
  );
};

// Mobile Sticky Book Button
const StickyBookButton = () => {
  const navigate = useNavigate();
  const location = useLocation();

  // Don't show on booking page or admin pages
  if (location.pathname === "/booking" || location.pathname.startsWith("/admin")) {
    return null;
  }

  return (
    <button
      onClick={() => navigate("/booking")}
      className="sticky-book-btn md:hidden bg-[#E10600] text-white font-bold py-3 px-6 rounded-full"
      data-testid="sticky-book-btn"
    >
      BOOK NOW
    </button>
  );
};

// Layout wrapper for public pages
const PublicLayout = ({ children }) => {
  return (
    <>
      <Navbar />
      <main className="pt-16 md:pt-20 min-h-screen">
        {children}
      </main>
      <Footer />
      <StickyBookButton />
    </>
  );
};

function App() {
  useEffect(() => {
    // Seed data on first load
    const seedData = async () => {
      try {
        await axios.post(`${API}/seed`);
      } catch (e) {
        console.log("Seed already done or error:", e.message);
      }
    };
    seedData();
  }, []);

  return (
    <div className="App">
      <BrowserRouter>
        <Toaster position="top-right" richColors />
        <Routes>
          {/* Public Routes */}
          <Route path="/" element={<PublicLayout><HomePage /></PublicLayout>} />
          <Route path="/training" element={<PublicLayout><TrainingPage /></PublicLayout>} />
          <Route path="/massage" element={<PublicLayout><MassagePage /></PublicLayout>} />
          <Route path="/media" element={<PublicLayout><MediaPage /></PublicLayout>} />
          <Route path="/merch" element={<PublicLayout><MerchPage /></PublicLayout>} />
          <Route path="/booking" element={<PublicLayout><BookingPage /></PublicLayout>} />
          <Route path="/booking/success" element={<PublicLayout><BookingPage /></PublicLayout>} />
          <Route path="/booking/cancel" element={<PublicLayout><BookingPage /></PublicLayout>} />
          <Route path="/contact" element={<PublicLayout><ContactPage /></PublicLayout>} />
          
          {/* Admin Routes */}
          <Route path="/admin/login" element={<AdminLoginPage />} />
          <Route path="/admin/*" element={<AdminDashboard />} />
        </Routes>
      </BrowserRouter>
    </div>
  );
}

export default App;
